'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, InputAdornment, TextField, debounce } from '@mui/material';
import { LuMapPin } from 'react-icons/lu';
import { loadPlacesLibrary } from './loadPlacesLibrary';
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;
/**
 * Google Places-backed location input. Falls back to free-text entry when the
 * Maps API key is missing or autocomplete fails — the typed value is always kept.
 */
export const LocationPicker = ({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  optionalHint
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const placesRef = useRef(null);
  const requestSeq = useRef(0);
  useEffect(() => {
    setInputValue(value);
  }, [value]);
  const fetchSuggestions = useMemo(() => debounce(async input => {
    const seq = ++requestSeq.current;
    try {
      if (!placesRef.current) placesRef.current = await loadPlacesLibrary();
      // No sessionToken → per-request billing (Essentials SKU, 10k free/month).
      const {
        suggestions
      } = await placesRef.current.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input
      });
      if (seq !== requestSeq.current) return;
      const texts = suggestions.map(s => s.placePrediction?.text.text).filter(t => Boolean(t));
      setOptions([...new Set(texts)]);
    } catch (error) {
      if (seq === requestSeq.current) setOptions([]);
      console.warn('[LocationPicker] Places autocomplete failed:', error);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, DEBOUNCE_MS), []);
  useEffect(() => () => fetchSuggestions.clear(), [fetchSuggestions]);
  return <Autocomplete freeSolo fullWidth size="small" disabled={disabled} forcePopupIcon={false} options={options} value={value} inputValue={inputValue} filterOptions={x => x} noOptionsText="No places found — your entry will be used as typed" onChange={(_e, newValue) => {
    const next = typeof newValue === 'string' ? newValue : '';
    setInputValue(next);
    onChange(next);
  }} onInputChange={(_e, nextInput, reason) => {
    setInputValue(nextInput);
    if (reason === 'reset' || reason === 'blur') return;
    onChange(nextInput);
    if (reason === 'clear' || nextInput.trim().length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchSuggestions(nextInput.trim());
  }} renderOption={(props, option) => {
    const {
      key,
      ...rest
    } = props;
    return <li key={key} {...rest}>
                        <span className="flex items-center gap-2 min-w-0">
                            <LuMapPin className="shrink-0 text-foreground-subtle" />
                            <span className="truncate">{option}</span>
                        </span>
                    </li>;
  }} renderInput={params => <TextField {...params} label={label} placeholder={placeholder} InputProps={{
    ...params.InputProps,
    startAdornment: <>
                                <InputAdornment position="start">
                                    <LuMapPin className="text-foreground-subtle text-base" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                            </>,
    endAdornment: <>
                                {loading ? <CircularProgress size={16} className="mr-1" /> : null}
                                {optionalHint ? <InputAdornment position="end">
                                        <span className="text-xs text-foreground-muted">Optional</span>
                                    </InputAdornment> : null}
                                {params.InputProps.endAdornment}
                            </>
  }} inputProps={{
    ...params.inputProps,
    'aria-label': label || 'Location',
    'data-testid': 'location-picker-input'
  }} />} data-testid="location-picker" />;
};